import Notification from 'antd/lib/notification';
import Table from 'antd/lib/table';
import { SiteAddress } from '../../site/SiteAddress';
import { SiteMarketAndLoadZone } from '../../site/SiteMarketAndLoadZone';
import { sortByField } from '../../table/columnFormatHelpers';
import { CustomizableColumnType } from '../../table/CustomizableTable';
import { WithEmptyDataTable } from '../../table/withEmptyDataTable';
import { UtilityOrIsoTag } from '../../UtilityOrIsoTag';
import { DEFAULT_DATE_FORMAT, getUtcFormattedTime } from '../../../domain/date/date';
import { ENROLLMENT_STATUS_TEXT_MAP } from '../../../domain/meter-enrollment/interface';
import { INomination, NOMINATION_RECENCY_TEXT_MAP } from '../../../domain/nomination/interface';
import { useNominationsListQuery } from '../../../domain/nomination/queries';
import { INominationReadinessTrack } from '../../../domain/nomination/readiness/interface';
import { numberFormatter } from '../../../domain/numberFormatter';

interface IReadinessTrackingNominationsTableProps {
    isRowExpanded: boolean;
    nominationReadinessTrack: INominationReadinessTrack;
}

export const ReadinessTrackingNominationsTable: React.FC<IReadinessTrackingNominationsTableProps> = ({ isRowExpanded, nominationReadinessTrack }) => {
    const [notification, notificationContextHolder] = Notification.useNotification();

    const { data, isLoading, isError, error } = useNominationsListQuery(
        { filter: { nominationIds: nominationReadinessTrack.nominationIds }, pagination: { current: 1, pageSize: 10000 } },
        { enable: isRowExpanded }
    );

    if (isError) {
        notification.error({ message: 'Error loading Nominations', description: error?.message || 'Unknown error' });
    }

    const nominations = data?.data || [];

    const nominationColumns: CustomizableColumnType<INomination>[] = [
        {
            key: 'iso',
            title: 'ISO',
            dataIndex: 'iso',
            sorter: (a, b) => sortByField(a, b, 'meter_enrollment.product.program.iso'),
            render: (text, record) => <UtilityOrIsoTag utilityOrIso={record.meter_enrollment?.product?.program?.iso} />,
            width: 100,
        },
        {
            key: 'utility_id',
            title: 'Utility',
            dataIndex: 'utility_id',
            sorter: (a, b) => sortByField(a, b, 'meter_enrollment.product.program.utility_id'),
            render: (text, record) => <UtilityOrIsoTag utilityOrIso={record.meter_enrollment?.product?.program?.utility_id} />,
            width: 100,
        },
        {
            key: 'lmp_market',
            title: 'Market, Load Zone',
            dataIndex: 'lmp_market',
            sorter: (a, b) => sortByField(a, b, 'meter_enrollment.meter.site'),
            render: (text, record) =>
                record.meter_enrollment?.meter?.site ? <SiteMarketAndLoadZone site={record.meter_enrollment?.meter?.site} /> : '',
        },
        {
            key: 'program_name',
            title: 'Program Name',
            dataIndex: 'program_name',
            sorter: (a, b) => sortByField(a, b, 'meter_enrollment.product.program.name'),
            render: (text, record) => record.meter_enrollment?.product?.program?.name,
            width: 160,
        },
        {
            key: 'control_provider_name',
            title: 'Control Provider',
            dataIndex: 'control_provider_name',
            sorter: (a, b) => sortByField(a, b, 'meter_enrollment.meter.site.control_provider.company_name'),
            render: (text, record) => record.meter_enrollment?.meter?.site?.control_provider?.company_name,
        },
        {
            key: 'site_name',
            title: 'Site Name',
            dataIndex: 'site_name',
            sorter: (a, b) => sortByField(a, b, 'meter_enrollment.meter.site.site_name'),
            render: (text, record) => record.meter_enrollment?.meter?.site?.site_name,
        },
        {
            key: 'site_store_number',
            title: 'Store #',
            dataIndex: 'site_store_number',
            sorter: (a, b) => sortByField(a, b, 'meter_enrollment.meter.site.site_store_number'),
            render: (text, record) => record.meter_enrollment?.meter?.site?.site_store_number,
        },
        {
            key: 'site_address',
            title: 'Address',
            dataIndex: 'site_address',
            render: (text, record) => (
                <SiteAddress
                    address={record.meter_enrollment?.meter?.site?.site_address}
                    city={record.meter_enrollment?.meter?.site?.site_city}
                    state={record.meter_enrollment?.meter?.site?.site_state}
                />
            ),
        },
        {
            key: 'enrollment_id',
            title: 'Enrollment ID',
            dataIndex: 'enrollment_id',
            sorter: (a, b) => sortByField(a, b, 'meter_enrollment.enrollment_id'),
            render: (text, record) => record.meter_enrollment?.enrollment_id,
        },
        {
            key: 'enrollment_status',
            title: 'Meter Enrollment Status',
            dataIndex: 'enrollment_status',
            sorter: (a, b) => sortByField(a, b, 'meter_enrollment.enrollment_status'),
            render: (text, record) => ENROLLMENT_STATUS_TEXT_MAP[record.meter_enrollment?.enrollment_status!],
        },
        {
            key: 'nomination_recency',
            title: 'Nomination Recency',
            dataIndex: 'nomination_recency',
            sorter: (a, b) => sortByField(a, b, 'recency'),
            render: (text, record) => NOMINATION_RECENCY_TEXT_MAP[record.recency!],
        },
        {
            key: 'start_date',
            title: 'Start Date',
            dataIndex: 'start_date',
            sorter: (a, b) => sortByField(a, b, 'start_date'),
            render: (text, record) => getUtcFormattedTime(record.start_date, DEFAULT_DATE_FORMAT),
        },
        {
            key: 'end_date',
            title: 'End Date',
            dataIndex: 'end_date',
            sorter: (a, b) => sortByField(a, b, 'end_date'),
            render: (text, record) => getUtcFormattedTime(record.end_date, DEFAULT_DATE_FORMAT),
        },
        {
            key: 'nomination_kw',
            title: 'Nominated kW',
            dataIndex: 'nomination_kw',
            sorter: (a, b) => sortByField(a, b, 'nomination_kw'),
            render: (text, record) => numberFormatter(record.nomination_kw),
            width: 120,
        },
    ];

    return (
        <div style={{ paddingTop: 16 }}>
            <WithEmptyDataTable condition={isLoading}>
                <Table
                    size="small"
                    rowKey="salesforce_id"
                    sticky={{ offsetHeader: 62 }}
                    loading={isLoading}
                    dataSource={nominations}
                    columns={nominationColumns}
                    pagination={{
                        pageSize: 5,
                        total: nominations.length,
                    }}
                />
            </WithEmptyDataTable>

            {notificationContextHolder}
        </div>
    );
};
